import { memo, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import ReviewItem from './ReviewItem';
import reviewService from '../../services/reviewService';
import './ReviewList.css';

/**
 * ReviewList Component
 *
 * Container for displaying reviews with:
 * - Sorting options (newest, oldest, highest, lowest, helpful)
 * - Filtering (by rating, verified only, with photos)
 * - Pagination
 * - Loading/empty/error states
 */
const ReviewList = memo(forwardRef(function ReviewList({
  productId,
  initialSortBy = 'newest',
  initialRatingFilter = null,
  currentUserId = null,
  onReviewAction,
  imageRefreshKey = null
}, ref) {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters and sorting
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [currentPage, setCurrentPage] = useState(1);
  const [ratingFilter, setRatingFilter] = useState(initialRatingFilter);

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: 10,
        sortBy: sortBy
      };

      if (ratingFilter) {
        params.rating = ratingFilter;
      }

      const response = await reviewService.getProductReviews(productId, params);
      setReviews(response.data || []);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Failed to load reviews:', err);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [productId, currentPage, sortBy, ratingFilter]);

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: fetchReviews
  }));

  // Fetch on mount and when dependencies change
  useEffect(() => {
    if (productId) {
      fetchReviews();
    }
  }, [productId, fetchReviews]);

  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1); // Reset to first page
    // Scroll to top of review list
    setTimeout(() => {
      document.querySelector('.review-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Handle rating filter (used by ReviewSummary component via callback)
  // eslint-disable-next-line no-unused-vars
  const handleRatingFilter = (rating) => {
    setRatingFilter(ratingFilter === rating ? null : rating);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of review list
    setTimeout(() => {
      document.querySelector('.review-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setRatingFilter(null);
    setSortBy('newest');
    setCurrentPage(1);
  };

  // Handle helpful click
  const handleHelpfulClick = async (reviewId) => {
    try {
      await reviewService.toggleHelpful(reviewId);
      // Refresh reviews to get updated helpful count
      fetchReviews();
      if (onReviewAction) {
        onReviewAction('helpful', reviewId);
      }
    } catch (err) {
      console.error('Failed to vote helpful:', err);
    }
  };

  // Render pagination
  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const { page, totalPages } = pagination;
    const pages = [];

    // Show max 5 page numbers
    let startPage = Math.max(1, page - 2);
    let endPage = Math.min(totalPages, page + 2);

    // Adjust if at edges
    if (page <= 2) {
      endPage = Math.min(5, totalPages);
    }
    if (page >= totalPages - 1) {
      startPage = Math.max(1, totalPages - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="review-list__pagination">
        <button
          className="review-list__page-btn"
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          aria-label="Previous page"
        >
          <i className="fas fa-chevron-left"></i>
        </button>

        {pages.map(pageNum => (
          <button
            key={pageNum}
            className={`review-list__page-btn ${page === pageNum ? 'review-list__page-btn--active' : ''}`}
            onClick={() => handlePageChange(pageNum)}
            aria-label={`Page ${pageNum}`}
            aria-current={page === pageNum ? 'page' : undefined}
          >
            {pageNum}
          </button>
        ))}

        <button
          className="review-list__page-btn"
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          aria-label="Next page"
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
    );
  };

  // Loading state
  if (loading && reviews.length === 0) {
    return (
      <div className="review-list review-list--loading">
        <div className="review-list__skeleton">
          {[1, 2, 3].map(i => (
            <div key={i} className="review-item-skeleton">
              <div className="skeleton skeleton--line"></div>
              <div className="skeleton skeleton--line skeleton--short"></div>
              <div className="skeleton skeleton--line"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="review-list review-list--error">
        <div className="review-list__error-state">
          <i className="fas fa-exclamation-triangle fa-3x"></i>
          <h4>{error}</h4>
          <button className="btn btn-primary" onClick={fetchReviews}>
            <i className="fas fa-redo"></i> Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (reviews.length === 0) {
    const isFiltered = ratingFilter !== null;

    return (
      <div className="review-list review-list--empty">
        <div className="review-list__empty-state">
          <i className="fas fa-comment-slash fa-3x"></i>
          <h4>{isFiltered ? 'No reviews match your filters' : 'No reviews yet'}</h4>
          <p>{isFiltered ? 'Try adjusting your filters' : 'Be the first to review this product!'}</p>
          {isFiltered && (
            <button className="btn btn-outline-primary" onClick={handleClearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  const hasActiveFilters = ratingFilter !== null || sortBy !== 'newest';

  return (
    <div className="review-list">
      {/* Header with controls */}
      <div className="review-list__header">
        <div className="review-list__count">
          {pagination && (
            <span>
              Showing {((pagination.page - 1) * pagination.limit) + 1}-
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} reviews
            </span>
          )}
        </div>

        <div className="review-list__controls">
          {/* Sort options */}
          <div className="review-list__sort">
            <label htmlFor="sort-select">Sort by:</label>
            <div className="review-list__sort-buttons">
              {[
                { value: 'newest', label: 'Newest', icon: 'fa-clock' },
                { value: 'oldest', label: 'Oldest', icon: 'fa-history' },
                { value: 'highest', label: 'Highest', icon: 'fa-arrow-up' },
                { value: 'lowest', label: 'Lowest', icon: 'fa-arrow-down' },
                { value: 'helpful', label: 'Most Helpful', icon: 'fa-thumbs-up' }
              ].map(option => (
                <button
                  key={option.value}
                  className={`review-list__sort-btn ${sortBy === option.value ? 'review-list__sort-btn--active' : ''}`}
                  onClick={() => handleSortChange(option.value)}
                  aria-pressed={sortBy === option.value}
                >
                  <i className={`fas ${option.icon}`}></i>
                  <span className="review-list__sort-label">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              className="review-list__clear-filters"
              onClick={handleClearFilters}
            >
              <i className="fas fa-times"></i> Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Active filter badges */}
      {ratingFilter && (
        <div className="review-list__active-filters">
          <span className="review-list__filter-badge">
            <i className="fas fa-star"></i> {ratingFilter} stars
            <button
              className="review-list__filter-remove"
              onClick={() => setRatingFilter(null)}
              aria-label="Remove rating filter"
            >
              ×
            </button>
          </span>
        </div>
      )}

      {/* Reviews */}
      <div className="review-list__items">
        {reviews.map(review => (
          <ReviewItem
            key={review.id}
            review={review}
            isOwnReview={currentUserId === review.userId}
            currentUserId={currentUserId}
            onHelpfulClick={handleHelpfulClick}
            onEditClick={(review) => onReviewAction && onReviewAction('edit', review)}
            onDeleteClick={(review) => onReviewAction && onReviewAction('delete', review)}
            onImageClick={(images, index) => onReviewAction && onReviewAction('viewImages', { images, index })}
            imageRefreshKey={imageRefreshKey}
          />
        ))}
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
}));

ReviewList.propTypes = {
  productId: PropTypes.number.isRequired,
  initialSortBy: PropTypes.oneOf(['newest', 'oldest', 'highest', 'lowest', 'helpful']),
  initialRatingFilter: PropTypes.number,
  currentUserId: PropTypes.number,
  onReviewAction: PropTypes.func,
  imageRefreshKey: PropTypes.number
};

export default ReviewList;
